"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
  Download,
  Headphones,
  AlertCircle,
} from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import { useSharedUiTranslations } from "@/components/i18n/ui-control-labels-context"


interface AudioPlayerProps {
  audioUrl: string
  conversationId: string
  callId?: number // Voice agent call ID for fetching signed URL
  pbxCallId?: string // PBX call ID (UUID) for fetching signed URL
  initialDuration?: number // Duration in seconds (from DB) to show before audio loads
}

export function AudioPlayer({ audioUrl, conversationId, callId, pbxCallId, initialDuration }: AudioPlayerProps) {
  const tUi = useSharedUiTranslations("AudioPlayer")

  const { fetchWithAuth } = useAuth()
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(initialDuration || 0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null)

  // Resolve storage:// URLs to signed URLs
  const resolveAudioUrl = useCallback(async (): Promise<string | null> => {
    // If we already resolved the URL, use it
    if (resolvedUrl) return resolvedUrl

    // If it's not a storage:// URL, use it directly
    if (!audioUrl.startsWith('storage://')) {
      setResolvedUrl(audioUrl)
      return audioUrl
    }

    // Need to fetch signed URL from API
    // Use PBX API for pbxCallId, Voice Agent API for callId
    if (pbxCallId) {
      try {
        const response = await fetchWithAuth(`/api/pbx/calls/${pbxCallId}/recording`)
        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || tUi("failedToLoadRecording"))
        }
        const data = await response.json()
        setResolvedUrl(data.url)
        return data.url
      } catch (err) {
        console.error('Failed to get PBX signed URL:', err)
        setError(err instanceof Error ? err.message : tUi("failedToLoadRecording"))
        return null
      }
    }

    if (callId) {
      try {
        const response = await fetchWithAuth(`/api/calls/${callId}/recording`)
        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || tUi("failedToLoadRecording"))
        }
        const data = await response.json()
        setResolvedUrl(data.url)
        return data.url
      } catch (err) {
        console.error('Failed to get signed URL:', err)
        setError(err instanceof Error ? err.message : tUi("failedToLoadRecording"))
        return null
      }
    }

    setError(tUi("cannotLoadRecordingMissingCallID"))
    return null
  }, [audioUrl, callId, pbxCallId, fetchWithAuth, resolvedUrl, tUi])

  // The element outlives the render that made it, so something has to end it.
  // Call Logs gave every row its own instance and never noticed; the unified inbox
  // keeps ONE player and swaps the recording under it, where a surviving element
  // means the header names one call and the speakers play another — and a
  // recording that keeps playing after the pane is gone has no controls left.
  const audioRef = useRef<HTMLAudioElement | null>(null)
  useEffect(() => {
    audioRef.current = audio
  }, [audio])

  // Cleanup runs both on a change of recording and on unmount, and by then the
  // ref still holds the element the OLD url made — which is the one to stop.
  useEffect(() => {
    return () => {
      audioRef.current?.pause()
      audioRef.current = null
    }
  }, [audioUrl])

  useEffect(() => {
    setAudio(null)
    setResolvedUrl(null)
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(initialDuration || 0)
    setError(null)
    setIsLoading(false)
  }, [audioUrl, initialDuration])

  const initializeAudio = async (autoPlay: boolean = false) => {
    if (!audio && audioUrl) {
      setIsLoading(true)

      const url = await resolveAudioUrl()
      if (!url) {
        setIsLoading(false)
        return
      }

      const audioElement = new Audio(url)
      audioElement.crossOrigin = "anonymous"

      audioElement.addEventListener("loadstart", () => setIsLoading(true))
      audioElement.addEventListener("canplay", () => {
        setIsLoading(false)
        if (autoPlay) {
          audioElement.play()
            .then(() => setIsPlaying(true))
            .catch((err) => {
              setError(tUi("failedToPlayAudio"))
              console.error("Play error:", err)
            })
        }
      })
      audioElement.addEventListener("loadedmetadata", () => {
        setDuration(audioElement.duration)
      })
      audioElement.addEventListener("timeupdate", () => {
        setCurrentTime(audioElement.currentTime)
      })
      audioElement.addEventListener("ended", () => {
        setIsPlaying(false)
        setCurrentTime(0)
      })
      audioElement.addEventListener("error", (e) => {
        setError(tUi("failedToLoadAudio"))
        setIsLoading(false)
        console.error("Audio error:", e)
      })

      audioElement.volume = volume
      audioElement.muted = isMuted
      setAudio(audioElement)
    }
  }

  const togglePlay = async () => {
    if (!audio) {
      await initializeAudio(true)
      return
    }

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio
        .play()
        .then(() => {
          setIsPlaying(true)
          setError(null)
        })
        .catch((err) => {
          setError(tUi("failedToPlayAudio"))
          console.error("Play error:", err)
        })
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = Number.parseFloat(e.target.value)
    setCurrentTime(newTime)
    if (audio) {
      audio.currentTime = newTime
    }
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number.parseFloat(e.target.value)
    setVolume(newVolume)
    if (audio) {
      audio.volume = newVolume
    }
  }

  const toggleMute = () => {
    const newMuted = !isMuted
    setIsMuted(newMuted)
    if (audio) {
      audio.muted = newMuted
    }
  }

  const resetAudio = () => {
    if (audio) {
      audio.currentTime = 0
      setCurrentTime(0)
      setIsPlaying(false)
    }
  }

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00"
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  const downloadAudio = async () => {
    const url = await resolveAudioUrl()
    if (!url) return

    const link = document.createElement("a")
    link.href = url
    link.download = `call-recording-${conversationId}.mp3`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0

  if (!audioUrl) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
        <Headphones className="w-4 h-4" />
        <span><span key={tUi("noAudioRecordingAvailable")}>{tUi("noAudioRecordingAvailable")}</span></span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-w-0 items-center gap-2 text-red-500 text-sm py-2">
        <AlertCircle className="w-4 h-4 shrink-0" />
        <span className="min-w-0 break-words">{error}</span>
        <Button variant="ghost" size="sm" onClick={() => setError(null)} className="shrink-0" aria-label={tUi("retryAudio")}>
          <RotateCcw className="w-3 h-3" />
        </Button>
      </div>
    )
  }

  return (
    <div className="min-w-0 w-full bg-gray-50 rounded-lg p-2 sm:p-3 mt-2">
      <div className="flex min-w-0 flex-wrap items-center gap-1.5 sm:flex-nowrap sm:gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={togglePlay}
          disabled={isLoading}
          aria-label={isPlaying ? "Pause recording" : "Play recording"}
          className="h-8 w-8 shrink-0 p-0 rounded-full bg-blue-100 hover:bg-blue-200"
        >
          {isLoading ? (
            <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-3 h-3 text-blue-600" />
          ) : (
            <Play className="w-3 h-3 text-blue-600 ml-0.5" />
          )}
        </Button>

        <Button variant="ghost" size="sm" onClick={resetAudio} className="h-8 w-8 shrink-0 p-0 sm:h-6 sm:w-6" aria-label={tUi("restartRecording")}>
          <RotateCcw className="w-3 h-3" />
        </Button>

        <div className="order-last flex w-full min-w-0 items-center gap-2 sm:order-none sm:w-auto sm:flex-1">
          <span className="shrink-0 text-xs tabular-nums text-gray-600 sm:w-10">{formatTime(currentTime)}</span>
          <input
            type="range"
            aria-label={tUi("recordingPosition")}
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="min-w-0 w-0 flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${progressPercent}%, #e5e7eb ${progressPercent}%, #e5e7eb 100%)`,
            }}
          />
          <span className="shrink-0 text-xs tabular-nums text-gray-600 sm:w-10">{formatTime(duration)}</span>
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1 sm:ml-0">
          <Button variant="ghost" size="sm" onClick={toggleMute} className="h-8 w-8 shrink-0 p-0 sm:h-6 sm:w-6" aria-label={isMuted ? "Unmute recording" : "Mute recording"}>
            {isMuted || volume === 0 ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
          </Button>
          <input
            type="range"
            aria-label={tUi("recordingVolume")}
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-12 min-w-0 sm:w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <Button variant="ghost" size="sm" onClick={downloadAudio} title={tUi("downloadAudio")} aria-label={tUi("downloadRecording")} className="h-8 w-8 shrink-0 p-0 sm:h-9 sm:w-auto sm:px-3">
          <Download className="w-3 h-3" />
        </Button>
      </div>
    </div>
  )
}
